import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Loader2, RefreshCw, IndianRupee,
  CheckCircle, Clock, AlertCircle, Users, Receipt,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from '@/hooks/use-toast';
import { useAuth } from '@/context/AuthContext';
import { usePermission } from '@/hooks/usePermission';
import {
  getAllBillingInvoices, getAppointmentsByDateRange, getDoctors,
  recordInvoicePayment,
} from '@/lib/api';
import { PaymentRecordModal } from '@/components/Billing/BillingModals';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmt(n: number) {
  return `₹${Math.round(n).toLocaleString('en-IN')}`;
}

function drName(name: string) {
  return name.startsWith('Dr.') ? name : `Dr. ${name}`;
}

type PaymentMode = 'cash' | 'upi' | 'card' | 'netbanking' | 'cheque' | 'advance' | 'other';
const MODE_MAP: Record<string, PaymentMode> = {
  cash: 'cash', upi: 'upi', card: 'card', cheque: 'cheque',
  netbanking: 'netbanking', 'bank transfer': 'netbanking',
  insurance: 'other', other: 'other',
};

const MODE_LABEL: Record<string, string> = {
  cash: 'Cash', upi: 'UPI', card: 'Card',
  netbanking: 'Net Banking', cheque: 'Cheque',
  advance: 'Advance', other: 'Other',
};

// ─── Stat card ────────────────────────────────────────────────────────────────

function StatCard({ label, value, sub, accent }: {
  label: string; value: string; sub?: string; accent?: string;
}) {
  return (
    <div className="bg-card border border-border rounded-xl p-4">
      <p className="text-xs text-muted-foreground mb-1">{label}</p>
      <p className={`text-2xl font-bold leading-none ${accent ?? 'text-foreground'}`}>{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function FrontDeskReportsPage() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { can } = usePermission();
  const clinicId = currentUser?.clinic_id ?? '';

  const [invoices, setInvoices] = useState<any[]>([]);
  const [appointments, setAppointments] = useState<any[]>([]);
  const [doctors, setDoctors] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [collectInvoice, setCollectInvoice] = useState<any>(null);

  const today = new Date().toISOString().split('T')[0];

  const load = async () => {
    if (!clinicId) return;
    try {
      const [invData, aptData, docData] = await Promise.all([
        can('billing:read') ? getAllBillingInvoices(clinicId, { dateFrom: today, dateTo: today }) : Promise.resolve([]),
        getAppointmentsByDateRange(today, today, clinicId),
        getDoctors(),
      ]);

      const invArr = Array.isArray(invData) ? invData : (invData as any)?.invoices ?? [];
      setInvoices(invArr);
      setAppointments(Array.isArray(aptData) ? aptData : []);
      setDoctors(Array.isArray(docData) ? docData : []);
    } catch {
      toast({ title: 'Error', description: 'Failed to load reports', variant: 'destructive' });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, [clinicId]);

  const handleRefresh = () => { setRefreshing(true); load(); };

  // ── Billing stats ────────────────────────────────────────────────────────

  const billing = useMemo(() => {
    const totalBilled    = invoices.reduce((s, inv) => s + Number(inv.total_amount || 0), 0);
    const collected      = invoices.reduce((s, inv) => s + Number(inv.paid_amount  || 0), 0);
    const outstanding    = totalBilled - collected;
    const paidCount      = invoices.filter(inv => inv.payment_status === 'paid').length;
    const partialCount   = invoices.filter(inv => inv.payment_status === 'partial').length;
    const unpaidCount    = invoices.filter(inv => inv.payment_status === 'unpaid').length;
    const avgBill        = invoices.length > 0 ? totalBilled / invoices.length : 0;
    return { totalBilled, collected, outstanding, paidCount, partialCount, unpaidCount, avgBill, total: invoices.length };
  }, [invoices]);

  // ── Doctor footfall ──────────────────────────────────────────────────────

  const footfall = useMemo(() => {
    const map: Record<string, { scheduled: number; completed: number; cancelled: number; total: number }> = {};
    appointments.forEach(apt => {
      const did = apt.doctor_id ?? 'unknown';
      if (!map[did]) map[did] = { scheduled: 0, completed: 0, cancelled: 0, total: 0 };
      map[did].total++;
      if (apt.status === 'completed' || apt.status === 'checked_in' || apt.status === 'in_progress') map[did].completed++;
      else if (apt.status === 'cancelled' || apt.status === 'no_show') map[did].cancelled++;
      else map[did].scheduled++;
    });
    return Object.entries(map).map(([doctorId, counts]) => ({
      doctorId,
      doctorName: doctors.find(d => d.id === doctorId)?.name ?? 'Unknown',
      ...counts,
    })).sort((a, b) => b.total - a.total);
  }, [appointments, doctors]);

  // ── Outstanding invoices ─────────────────────────────────────────────────

  const unpaidInvoices = useMemo(() =>
    invoices
      .filter(inv => inv.payment_status === 'unpaid' || inv.payment_status === 'partial')
      .sort((a, b) => Number(b.total_amount) - Number(a.total_amount)),
  [invoices]);

  // ── Collect payment ──────────────────────────────────────────────────────

  const handleCollect = async (amount: number, method: string) => {
    if (!collectInvoice) return;
    try {
      await recordInvoicePayment(collectInvoice.id, {
        amount,
        payment_mode: (MODE_MAP[method?.toLowerCase()] ?? 'cash') as PaymentMode,
        payment_date: new Date().toISOString(),
      });
      toast({ title: 'Payment recorded', description: `${fmt(amount)} collected` });
      setCollectInvoice(null);
      load();
    } catch (err: unknown) {
      toast({ title: 'Error', description: err instanceof Error ? err.message : 'Failed', variant: 'destructive' });
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="prescription-form-container flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="prescription-form-container space-y-6">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/frontdesk')}
            className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-foreground">Today's Summary</h1>
            <p className="text-sm text-muted-foreground">
              {new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing} className="gap-2">
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* ── Billing KPIs ──────────────────────────────────────────────────── */}
      {can('billing:read') ? (
        <section>
          <h2 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-2">
            <IndianRupee className="w-4 h-4" /> Billing
          </h2>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <StatCard label="Total billed"   value={fmt(billing.totalBilled)} sub={`${billing.total} invoice${billing.total !== 1 ? 's' : ''}`} />
            <StatCard label="Collected"      value={fmt(billing.collected)}   accent="text-success" />
            <StatCard label="Outstanding"    value={fmt(billing.outstanding)} accent={billing.outstanding > 0 ? 'text-destructive' : 'text-muted-foreground'} />
            <StatCard label="Avg bill"       value={fmt(billing.avgBill)}     sub="per invoice" />
          </div>

          {/* Payment status breakdown */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-success/10 border border-success/20 text-xs font-medium text-success">
              <CheckCircle className="w-3.5 h-3.5" />
              Paid: {billing.paidCount}
            </div>
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-warning/10 border border-warning/20 text-xs font-medium text-warning">
              <Clock className="w-3.5 h-3.5" />
              Partial: {billing.partialCount}
            </div>
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-destructive/10 border border-destructive/20 text-xs font-medium text-destructive">
              <AlertCircle className="w-3.5 h-3.5" />
              Unpaid: {billing.unpaidCount}
            </div>
            {billing.total === 0 && (
              <p className="text-sm text-muted-foreground">No invoices created today</p>
            )}
          </div>
        </section>
      ) : (
        <p className="text-sm text-muted-foreground">No permission to view billing data.</p>
      )}

      {/* ── Doctor footfall ───────────────────────────────────────────────── */}
      <section>
        <h2 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-2">
          <Users className="w-4 h-4" /> OPD Footfall
        </h2>

        {footfall.length === 0 ? (
          <p className="text-sm text-muted-foreground">No appointments today</p>
        ) : (
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            {/* Header row */}
            <div className="grid grid-cols-5 gap-2 px-4 py-2 bg-muted/30 border-b border-border text-xs font-semibold text-muted-foreground">
              <div className="col-span-2">Doctor</div>
              <div className="text-center">Scheduled</div>
              <div className="text-center">Seen</div>
              <div className="text-center">Cancelled</div>
            </div>
            {footfall.map(row => (
              <div key={row.doctorId} className="grid grid-cols-5 gap-2 px-4 py-3 border-b border-border/50 last:border-0 items-center">
                <div className="col-span-2 font-medium text-sm">{drName(row.doctorName)}</div>
                <div className="text-center">
                  <span className="text-sm font-bold text-primary">{row.total}</span>
                </div>
                <div className="text-center">
                  <span className="text-sm font-bold text-success">{row.completed}</span>
                </div>
                <div className="text-center">
                  <span className={`text-sm font-bold ${row.cancelled > 0 ? 'text-destructive' : 'text-muted-foreground'}`}>
                    {row.cancelled}
                  </span>
                </div>
              </div>
            ))}
            {/* Totals row */}
            {footfall.length > 1 && (
              <div className="grid grid-cols-5 gap-2 px-4 py-2.5 bg-muted/20 border-t border-border text-xs font-bold text-muted-foreground">
                <div className="col-span-2">Total</div>
                <div className="text-center text-foreground">{appointments.length}</div>
                <div className="text-center text-success">
                  {appointments.filter(a => ['completed', 'checked_in', 'in_progress'].includes(a.status)).length}
                </div>
                <div className="text-center text-destructive">
                  {appointments.filter(a => ['cancelled', 'no_show'].includes(a.status)).length}
                </div>
              </div>
            )}
          </div>
        )}
      </section>

      {/* ── Outstanding invoices ──────────────────────────────────────────── */}
      {can('billing:read') && unpaidInvoices.length > 0 && (
        <section>
          <h2 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-2">
            <Receipt className="w-4 h-4" /> Outstanding Today ({unpaidInvoices.length})
          </h2>
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            {unpaidInvoices.map((inv, i) => (
              <div
                key={inv.id}
                className={`flex items-center justify-between px-4 py-3 ${i < unpaidInvoices.length - 1 ? 'border-b border-border/50' : ''}`}
              >
                <div className="min-w-0">
                  <p className="text-sm font-semibold truncate">{inv.patient_name ?? inv.invoice_number}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs text-muted-foreground">{inv.invoice_number}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                      inv.payment_status === 'partial' ? 'bg-warning/10 text-warning' : 'bg-destructive/10 text-destructive'
                    }`}>
                      {inv.payment_status === 'partial' ? 'Partial' : 'Unpaid'}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <div className="text-right">
                    <p className="text-sm font-bold text-destructive">
                      {fmt(Number(inv.total_amount) - Number(inv.paid_amount || 0))}
                    </p>
                    {inv.payment_status === 'partial' && (
                      <p className="text-[10px] text-muted-foreground">of {fmt(Number(inv.total_amount))}</p>
                    )}
                  </div>
                  {can('billing:write') && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs text-warning border-warning/30 hover:bg-warning/10"
                      onClick={() => setCollectInvoice(inv)}
                    >
                      Collect
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <PaymentRecordModal
        isOpen={!!collectInvoice}
        onClose={() => setCollectInvoice(null)}
        invoice={collectInvoice}
        onRecordPayment={handleCollect}
        loading={false}
      />
    </div>
  );
}
